/* eslint-disable react-hooks/exhaustive-deps */
/**
 * StockSearch — Phase 1.3 stock photo ingestion UI.
 *
 * Dedicated full-page surface for operator-curated stock library growth.
 * Lives at /admin/brand-assets/stock-search. Admin-only on the backend
 * (require_admin gate on /stock/search and /stock/approve).
 *
 * Flow:
 *   1. Operator types a query, picks providers (default both)
 *   2. Hit Search → backend hits Unsplash + Pexels in parallel, cross-source dedupes
 *   3. Results render grouped by source (Remi's UX feedback: attribution
 *      context must be visible at approval time, not just retrievable after)
 *   4. Operator selects N candidates with checkboxes, picks target pool
 *      (default 'subjects'), clicks Approve → backend downloads,
 *      uploads to object store, INSERTs brand_assets row, runs Phase 1.4
 *      classifier inline, auto-activates on verdict='safe'
 *   5. Toast confirms ingest count + classifier verdicts; pool-collision
 *      warnings surface as yellow toasts (operator already confirmed)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Search as SearchIcon,
  Camera,
  Loader2,
  ImageOff,
  ExternalLink,
  CheckCircle2,
  Circle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

// Default workspace — mirrors BrandAssetGallery default. Real production pool.
const DEFAULT_WS = "a24c8722-c708-4511-a5cc-8b98a884bbfb";

// Pool taxonomy (mirrors backend _VALID_POOLS in routes/stock_routes.py).
// 'subjects' is the most common choice for environmental stock; defaults
// to it per Remi's spec answer (#2).
const POOLS = [
  "subjects",
  "backgrounds",
  "decorations",
  "illustrations",
  "lockups",
];

const SOURCE_PILL = {
  unsplash: "bg-black text-white border-0",
  pexels: "bg-teal-600 text-white border-0",
};

function authedFetch(url, options = {}) {
  return fetch(url, { credentials: "include", ...options });
}


// ─── Quota chip — surfaces month-to-date usage ──────────────────────────


function QuotaChip({ source, quota }) {
  if (!quota) return null;
  const pct = quota.percent ?? 0;
  const tone =
    pct >= 80 ? "bg-rose-100 text-rose-900 border-rose-300" :
    pct >= 50 ? "bg-amber-100 text-amber-900 border-amber-300" :
    "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <Badge
      variant="outline"
      className={`${tone} text-[11px]`}
      data-testid={`stock-quota-${source}`}
      title={`${source} monthly cap: ${quota.cap?.toLocaleString()} requests`}
    >
      {source}: {quota.used?.toLocaleString()} / {quota.cap?.toLocaleString()} ({pct}%)
    </Badge>
  );
}


// ─── Candidate card — single search result tile ─────────────────────────


function CandidateCard({ candidate, selected, onToggle, onPreview }) {
  const [broken, setBroken] = useState(false);
  const key = `${candidate.source}:${candidate.source_id}`;
  return (
    <div
      className={`relative group rounded-lg border bg-white overflow-hidden transition ${
        selected ? "ring-2 ring-blue-500 border-blue-500" : "border-slate-200 hover:border-slate-300"
      }`}
      data-testid={`stock-candidate-${key}`}
    >
      <button
        type="button"
        onClick={() => onPreview(candidate)}
        className="block w-full aspect-square bg-slate-100 cursor-pointer"
        data-testid={`stock-candidate-preview-${key}`}
      >
        {broken || !candidate.thumb_url ? (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <ImageOff className="h-8 w-8" />
          </div>
        ) : (
          <img
            src={candidate.thumb_url}
            alt={candidate.description || candidate.source_id}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setBroken(true)}
          />
        )}
      </button>

      {/* Source pill */}
      <div className="absolute top-2 left-2">
        <Badge
          className={`${SOURCE_PILL[candidate.source] || "bg-slate-700 text-white border-0"} text-[10px]`}
          data-testid={`stock-candidate-source-${key}`}
        >
          <Camera className="h-2.5 w-2.5 mr-1" />
          {candidate.source === "unsplash" ? "Unsplash" : "Pexels"}
        </Badge>
      </div>

      {/* Select checkbox */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(candidate);
        }}
        className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow hover:bg-white"
        data-testid={`stock-candidate-select-${key}`}
        aria-label={selected ? "Deselect" : "Select"}
      >
        {selected ? (
          <CheckCircle2 className="h-5 w-5 text-blue-600" />
        ) : (
          <Circle className="h-5 w-5 text-slate-400" />
        )}
      </button>

      <div className="p-2 text-xs space-y-0.5">
        <div className="truncate text-slate-700" title={candidate.description || ""}>
          {candidate.description || "(no description)"}
        </div>
        <div className="truncate text-slate-500 italic">
          {candidate.attribution_text}
        </div>
      </div>
    </div>
  );
}


// ─── Preview modal — bigger view of one candidate ───────────────────────


function CandidatePreview({ candidate, onClose }) {
  if (!candidate) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stock-candidate-preview-modal"
    >
      <div
        className="bg-white rounded-lg overflow-hidden max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-3 border-b border-slate-200">
          <div className="text-sm">
            <div className="font-medium italic">{candidate.attribution_text}</div>
            {candidate.description ? (
              <div className="text-slate-600 text-xs mt-0.5">{candidate.description}</div>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="stock-candidate-preview-close"
          >
            Close
          </Button>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center min-h-[400px]">
          <img
            src={candidate.preview_url}
            alt={candidate.description || candidate.source_id}
            className="max-w-full max-h-[70vh] object-contain"
          />
        </div>
        <div className="p-3 border-t border-slate-200 text-xs flex flex-wrap gap-3">
          {candidate.photographer_url ? (
            <a
              href={candidate.photographer_url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> Photographer
            </a>
          ) : null}
          {candidate.source_url ? (
            <a
              href={candidate.source_url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> View on{" "}
              {candidate.source === "unsplash" ? "Unsplash" : "Pexels"}
            </a>
          ) : null}
          <span className="text-slate-500">
            {candidate.width} × {candidate.height}
          </span>
        </div>
      </div>
    </div>
  );
}


// ─── Main page ───────────────────────────────────────────────────────────


export default function StockSearch() {
  const workspaceId = DEFAULT_WS;

  const [query, setQuery] = useState("");
  const [sources, setSources] = useState({ unsplash: true, pexels: true });
  const [pool, setPool] = useState("subjects");
  const [results, setResults] = useState(null); // {candidates, quota, ...} | null
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState({}); // key → candidate
  const [previewCandidate, setPreviewCandidate] = useState(null);
  const [approving, setApproving] = useState(false);
  const [initialQuota, setInitialQuota] = useState(null);

  // Load initial quota snapshot on mount so the chips render before first search.
  useEffect(() => {
    authedFetch(`${API}/v2/workspaces/${workspaceId}/stock/quota`)
      .then((r) => r.json())
      .then((d) => setInitialQuota(d))
      .catch(() => setInitialQuota(null));
  }, []);

  const quota = results?.quota || initialQuota;

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) {
      toast.warning("Please enter a search query");
      return;
    }
    const srcList = Object.entries(sources)
      .filter(([_, on]) => on)
      .map(([k]) => k);
    if (srcList.length === 0) {
      toast.warning("Select at least one source");
      return;
    }

    setLoading(true);
    try {
      const url = `${API}/v2/workspaces/${workspaceId}/stock/search?q=${encodeURIComponent(query)}&sources=${srcList.join(",")}&per_page=20`;
      const r = await authedFetch(url);
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.detail?.message || data?.detail || `HTTP ${r.status}`);
      }
      setResults(data);
      // Selection state intentionally preserved across new searches —
      // operator can build up a multi-query batch before approving.
      if ((data.candidates || []).length === 0) {
        toast.message("No results", { description: "Try a different query or check another provider." });
      }
      if ((data.provider_errors || []).length > 0) {
        const probs = data.provider_errors.map((e) => `${e.source}: ${e.error}`).join("; ");
        toast.warning(`Provider issue(s): ${probs}`);
      }
      if ((data.capped_sources || []).length > 0) {
        toast.warning(`Capped this month: ${data.capped_sources.join(", ")}`);
      }
    } catch (err) {
      toast.error(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const candidateKey = (c) => `${c.source}:${c.source_id}`;

  const toggleCandidate = (candidate) => {
    const key = candidateKey(candidate);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = candidate;
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedKeys(new Set());
    setSelectedItems({});
  };

  // Group candidates by source for visual clarity (Remi's UX refinement #4):
  // attribution context differs per source so the source needs to be visible
  // at approval time, not just retrievable after.
  const grouped = useMemo(() => {
    const out = { unsplash: [], pexels: [] };
    for (const c of results?.candidates || []) {
      if (out[c.source]) out[c.source].push(c);
    }
    return out;
  }, [results]);

  const handleApprove = async () => {
    const items = Object.values(selectedItems);
    if (items.length === 0) {
      toast.warning("Nothing selected to approve");
      return;
    }
    setApproving(true);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/stock/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            search_query: query,
            approvals: items.map((c) => ({
              source: c.source,
              source_id: c.source_id,
              full_url: c.full_url,
              pool,
              photographer_name: c.photographer_name,
              photographer_username: c.photographer_username,
              photographer_url: c.photographer_url,
              source_url: c.source_url,
              description: c.description,
              attribution_required: c.attribution_required,
              attribution_text: c.attribution_text,
              width: c.width,
              height: c.height,
            })),
          }),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.detail || `HTTP ${r.status}`);
      }
      const ingested = data.ingested || [];
      const safeN = ingested.filter((a) => a.is_active).length;
      const reviewN = ingested.length - safeN;
      toast.success(
        `Ingested ${ingested.length} of ${items.length}` +
          (safeN ? ` · ${safeN} auto-activated (safe)` : "") +
          (reviewN ? ` · ${reviewN} pending review` : "")
      );
      if ((data.pool_collision_warnings || []).length > 0) {
        toast.warning(
          `${data.pool_collision_warnings.length} pool-collision warning(s) — see existing brand_assets near-duplicates`,
          { duration: 8000 }
        );
      }
      if ((data.failures || []).length > 0) {
        data.failures.forEach((f) =>
          toast.error(`Failed ${f.source}:${f.source_id} — ${f.error?.slice(0, 80)}`)
        );
      }
      if (data.quota) {
        setInitialQuota(data.quota);
        setResults((prev) => prev ? { ...prev, quota: data.quota } : prev);
      }
      // Drop ingested-or-failed selections; leave the rest selected so
      // operator can continue refining.
      const handledKeys = new Set([
        ...(ingested.map((a) => `${a.source}:${a.source_id}`)),
        ...((data.failures || []).map((f) => `${f.source}:${f.source_id}`)),
      ]);
      setSelectedKeys((prev) => new Set([...prev].filter((k) => !handledKeys.has(k))));
      setSelectedItems((prev) => {
        const next = {};
        for (const k of Object.keys(prev)) if (!handledKeys.has(k)) next[k] = prev[k];
        return next;
      });
    } catch (err) {
      toast.error(`Approve failed: ${err.message}`);
    } finally {
      setApproving(false);
    }
  };

  const selectedCount = selectedKeys.size;

  return (
    <div
      className="container mx-auto p-4 sm:p-6 max-w-[1600px] pb-24"
      data-testid="stock-search-page"
    >
      {/* Header */}
      <div className="flex items-baseline justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <Link
              to="/admin/brand-assets"
              className="hover:underline inline-flex items-center gap-1"
              data-testid="stock-search-back"
            >
              <ArrowLeft className="h-3 w-3" /> Brand asset gallery
            </Link>
          </div>
          <h1 className="text-2xl font-semibold">Stock photo search</h1>
          <p className="text-sm text-slate-500">
            Search Unsplash + Pexels. Pick keepers. They land in your pool as{" "}
            <code className="text-xs">origin='stock'</code>, classified and
            auto-activated if safe.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="stock-quota-row">
          <QuotaChip source="unsplash" quota={quota?.unsplash} />
          <QuotaChip source="pexels" quota={quota?.pexels} />
        </div>
      </div>

      {/* Search form */}
      <form
        onSubmit={handleSearch}
        className="border border-slate-200 bg-white rounded-lg p-4 mb-6 space-y-3"
        data-testid="stock-search-form"
      >
        <div className="flex gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. institutional architecture, london"
              className="pl-9"
              data-testid="stock-search-input"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="stock-search-submit"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <SearchIcon className="h-4 w-4 mr-1" />
            )}
            Search
          </Button>
        </div>
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="text-slate-500">Sources:</span>
          {["unsplash", "pexels"].map((s) => (
            <label
              key={s}
              className="flex items-center gap-1.5 cursor-pointer"
              data-testid={`stock-source-toggle-${s}`}
            >
              <Checkbox
                checked={sources[s]}
                onCheckedChange={(v) => setSources({ ...sources, [s]: !!v })}
              />
              <span className="capitalize">{s}</span>
            </label>
          ))}
        </div>
      </form>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : results === null ? (
        <div
          className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500"
          data-testid="stock-search-empty"
        >
          Search above to discover stock photos. Results group by source so
          you can see attribution context at a glance.
        </div>
      ) : results.candidates.length === 0 ? (
        <div
          className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500"
          data-testid="stock-search-no-results"
        >
          No candidates for "{results.query}". Try a different query or check
          another provider.
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-3" data-testid="stock-results-summary">
            {results.count} candidate{results.count === 1 ? "" : "s"}
            {results.dedupe_dropped > 0
              ? ` (${results.dedupe_dropped} cross-source duplicate${results.dedupe_dropped === 1 ? "" : "s"} dropped)`
              : null}
          </div>

          {["unsplash", "pexels"].map((src) => {
            const group = grouped[src];
            if (!group || group.length === 0) return null;
            return (
              <section key={src} className="mb-6" data-testid={`stock-results-group-${src}`}>
                <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${
                    src === "unsplash" ? "bg-black" : "bg-teal-600"
                  }`} />
                  {src === "unsplash" ? "Unsplash" : "Pexels"} ({group.length})
                  {src === "unsplash" ? (
                    <Badge className="bg-amber-100 text-amber-900 border-0 text-[10px]">
                      Attribution required
                    </Badge>
                  ) : null}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {group.map((c) => (
                    <CandidateCard
                      key={`${c.source}:${c.source_id}`}
                      candidate={c}
                      selected={selectedKeys.has(`${c.source}:${c.source_id}`)}
                      onToggle={toggleCandidate}
                      onPreview={setPreviewCandidate}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      <CandidatePreview candidate={previewCandidate} onClose={() => setPreviewCandidate(null)} />

      {/* Sticky approval footer */}
      {selectedCount > 0 ? (
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40 px-4 sm:px-6 py-3"
          data-testid="stock-approve-footer"
        >
          <div className="container mx-auto max-w-[1600px] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium" data-testid="stock-selected-count">
              <CheckCircle2 className="inline h-4 w-4 mr-1 text-blue-600" />
              {selectedCount} selected
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Target pool:</span>
                <Select value={pool} onValueChange={setPool}>
                  <SelectTrigger
                    className="w-[160px] h-8 text-sm"
                    data-testid="stock-pool-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POOLS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearSelection}
                disabled={approving}
                data-testid="stock-clear-selection"
              >
                Clear
              </Button>
              <Button
                onClick={handleApprove}
                disabled={approving}
                data-testid="stock-approve-batch-btn"
              >
                {approving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Approving…
                  </>
                ) : (
                  <>
                    Approve & ingest {selectedCount}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Heads-up when both pHash & classifier are about to run inline. */}
      {selectedCount >= 5 && !approving ? (
        <div
          className="fixed bottom-20 right-4 max-w-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 shadow"
          data-testid="stock-batch-heads-up"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Heads up</div>
              The approve flow runs the vision classifier on each
              photo (~10-15s each). {selectedCount} photos ≈ {selectedCount * 12}s.
              The browser will wait — don't close this tab.
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
